/**
 * React Query hooks for scorecard operations.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { mlbApi } from '../services/api';
import { scorecardPagesKeys } from './useScorecardPages';
import { STALE_TIMES, QUERY_RETRY_CONFIG } from '../types/statcast';
import { useTrackEvent, AnalyticsEventType } from './useAnalytics';
import type {
  ScorecardListResponse,
  ScorecardDetailResponse,
  FeaturedListResponse,
  CommentListResponse,
  ModerationQueueResponse,
  FlaggedCommentsResponse,
  LikedScorecardsResponse,
  ModerationHistoryResponse,
} from '../services/api';

// =============================================================================
// Query Keys
// =============================================================================

export const scorecardKeys = {
  all: ['scorecards'] as const,
  game: (gameId: number) => [...scorecardKeys.all, 'game', gameId] as const,
  user: (userId: number) => [...scorecardKeys.all, 'user', userId] as const,
  mine: () => [...scorecardKeys.all, 'mine'] as const,
  liked: () => [...scorecardKeys.all, 'liked'] as const,
  detail: (id: number) => [...scorecardKeys.all, 'detail', id] as const,
  featured: () => [...scorecardKeys.all, 'featured'] as const,
  comments: (scorecardId: number) => [...scorecardKeys.all, 'comments', scorecardId] as const,
  moderationQueue: () => [...scorecardKeys.all, 'moderation-queue'] as const,
  flaggedComments: () => [...scorecardKeys.all, 'flagged-comments'] as const,
  spotlightWeek: (weekStart: string) =>
    [...scorecardKeys.all, 'spotlight-week', weekStart] as const,
  spotlightSuggestions: (targetDate: string) =>
    [...scorecardKeys.all, 'spotlight-suggestions', targetDate] as const,
  momentUploads: (category: string) => [...scorecardKeys.all, 'moment-uploads', category] as const,
  editorPickQueue: () => [...scorecardKeys.all, 'editor-pick-queue'] as const,
  editorPickSuggestions: () => [...scorecardKeys.all, 'editor-pick-suggestions'] as const,
};

// =============================================================================
// Scorecard Query Hooks
// =============================================================================

/**
 * Fetch scorecards for a specific game.
 */
export function useGameScorecards(
  gameId: number,
  params: { limit?: number; offset?: number } = {}
) {
  const { getToken } = useAuth();

  return useQuery<ScorecardListResponse>({
    queryKey: [...scorecardKeys.game(gameId), params],
    queryFn: async () => {
      const token = await getToken();
      return mlbApi.getGameScorecards(gameId, params, token ?? undefined);
    },
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch scorecards for a specific user.
 */
export function useUserScorecards(
  userId: number,
  params: { limit?: number; offset?: number } = {}
) {
  const { getToken } = useAuth();

  return useQuery<ScorecardListResponse>({
    queryKey: [...scorecardKeys.user(userId), params],
    queryFn: async () => {
      const token = await getToken();
      return mlbApi.getUserScorecards(userId, params, token ?? undefined);
    },
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch current user's scorecards.
 */
export function useMyScorecards(params: { limit?: number; offset?: number } = {}) {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<ScorecardListResponse>({
    queryKey: [...scorecardKeys.mine(), params],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getMyScorecards(params, token);
    },
    enabled: isSignedIn,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch scorecards liked by the current user.
 */
export function useLikedScorecards(params: { limit?: number; offset?: number } = {}) {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<LikedScorecardsResponse>({
    queryKey: [...scorecardKeys.liked(), params],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getLikedScorecards(params, token);
    },
    enabled: isSignedIn,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch featured scorecards for homepage.
 */
export function useFeaturedScorecards(limit: number = 20) {
  return useQuery<FeaturedListResponse>({
    queryKey: [...scorecardKeys.featured(), limit],
    queryFn: () => mlbApi.getFeaturedScorecards(limit),
    staleTime: STALE_TIMES.SCHEDULE,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch single scorecard details.
 */
export function useScorecardDetail(scorecardId: number) {
  const { getToken } = useAuth();

  return useQuery<ScorecardDetailResponse>({
    queryKey: scorecardKeys.detail(scorecardId),
    queryFn: async () => {
      const token = await getToken();
      return mlbApi.getScorecardDetail(scorecardId, token ?? undefined);
    },
    staleTime: STALE_TIMES.LIVE_DATA,
    enabled: scorecardId > 0, // Don't fetch if no scorecard selected
    ...QUERY_RETRY_CONFIG,
  });
}

// =============================================================================
// Scorecard Mutation Hooks
// =============================================================================

/**
 * Upload a new scorecard.
 */
export function useUploadScorecard() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      const token = await getToken();
      return mlbApi.uploadScorecard(formData, token ?? undefined);
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: scorecardKeys.all });
      // Also invalidate scorecard pages (My Scorecards, etc.)
      queryClient.invalidateQueries({ queryKey: scorecardPagesKeys.myProfile() });
      queryClient.invalidateQueries({ queryKey: scorecardPagesKeys.all });
      // Invalidate community stats (MVP votes, fan affiliation, rooting for) on gameday cards + boxscore
      queryClient.invalidateQueries({ queryKey: ['game-mvp-votes'] });
      return data;
    },
  });
}

/**
 * Check if user already has a scorecard for a game.
 */
export function useCheckExistingScorecard() {
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (gameId: number) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.checkExistingScorecard(gameId, token);
    },
  });
}

/**
 * Update scorecard metadata.
 */
export function useUpdateScorecard() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      scorecardId,
      data,
    }: {
      scorecardId: number;
      data: {
        is_public?: boolean;
        attended_in_person?: boolean;
        caption?: string;
        viewing_method?: string | null;
        scoring_tool?: string | null;
        scorebook_type?: string | null;
        game_outcome?: string | null;
        mvp_player_mlb_id?: number | null;
        mvp_player_name?: string | null;
      };
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.updateScorecard(scorecardId, data, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.detail(variables.scorecardId) });
      queryClient.invalidateQueries({ queryKey: scorecardKeys.mine() });
    },
  });
}

/**
 * Delete a scorecard.
 */
export function useDeleteScorecard() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scorecardId: number) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.deleteScorecard(scorecardId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.all });
      queryClient.invalidateQueries({ queryKey: scorecardPagesKeys.myProfile() });
    },
  });
}

/**
 * Toggle like on a scorecard.
 */
export function useToggleLike() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const trackEvent = useTrackEvent();

  return useMutation({
    mutationFn: async (scorecardId: number) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.toggleScorecardLike(scorecardId, token);
    },
    onSuccess: (data, scorecardId) => {
      // Track like/unlike analytics event
      trackEvent({
        eventType: data.action === 'liked' ? AnalyticsEventType.LIKE : AnalyticsEventType.UNLIKE,
        targetType: 'scorecard',
        targetId: scorecardId,
      });
      // Update the scorecard detail cache
      queryClient.setQueryData<ScorecardDetailResponse>(
        scorecardKeys.detail(scorecardId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            like_count: data.like_count,
            is_liked: data.action === 'liked',
          };
        }
      );
      // Invalidate list queries to update like counts
      queryClient.invalidateQueries({ queryKey: scorecardKeys.all });
    },
  });
}

export type SharePlatform = 'x' | 'facebook' | 'instagram' | 'tiktok' | 'link';

/**
 * Track a share action for analytics.
 */
export function useTrackShare() {
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async ({
      scorecardId,
      platform,
    }: {
      scorecardId: number;
      platform: SharePlatform;
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.trackScorecardShare(scorecardId, platform, token);
    },
  });
}

/**
 * Check for duplicate scorecard.
 */
export function useCheckDuplicate() {
  return useMutation({
    mutationFn: (formData: FormData) => mlbApi.checkDuplicateScorecard(formData),
  });
}

// =============================================================================
// Comment Hooks
// =============================================================================

/**
 * Fetch comments for a scorecard.
 */
export function useScorecardComments(
  scorecardId: number,
  params: { limit?: number; offset?: number } = {}
) {
  return useQuery<CommentListResponse>({
    queryKey: [...scorecardKeys.comments(scorecardId), params],
    queryFn: () => mlbApi.getScorecardComments(scorecardId, params),
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Add a comment to a scorecard.
 */
export function useAddComment() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const trackEvent = useTrackEvent();

  return useMutation({
    mutationFn: async ({
      scorecardId,
      body,
      parentId,
    }: {
      scorecardId: number;
      body: string;
      parentId?: number;
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.addScorecardComment(scorecardId, body, token, parentId);
    },
    onSuccess: (_, variables) => {
      trackEvent({
        eventType: AnalyticsEventType.COMMENT,
        targetType: 'scorecard',
        targetId: variables.scorecardId,
        eventData: { is_reply: !!variables.parentId },
      });
      queryClient.invalidateQueries({ queryKey: scorecardKeys.comments(variables.scorecardId) });
      queryClient.invalidateQueries({ queryKey: scorecardKeys.detail(variables.scorecardId) });
    },
  });
}

/**
 * Delete a comment.
 */
export function useDeleteComment() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scorecardId, commentId }: { scorecardId: number; commentId: number }) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.deleteScorecardComment(scorecardId, commentId, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.comments(variables.scorecardId) });
      queryClient.invalidateQueries({ queryKey: scorecardKeys.detail(variables.scorecardId) });
    },
  });
}

/**
 * Report a comment.
 */
export function useReportComment() {
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async ({
      scorecardId,
      commentId,
      reason,
      details,
    }: {
      scorecardId: number;
      commentId: number;
      reason: 'spam' | 'harassment' | 'inappropriate' | 'other';
      details?: string;
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.reportScorecardComment(scorecardId, commentId, reason, token, details);
    },
  });
}

// =============================================================================
// Admin Hooks
// =============================================================================

/**
 * Fetch moderation queue (admin only).
 */
export function useModerationQueue(
  params: { status?: string; limit?: number; offset?: number } = {}
) {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<ModerationQueueResponse>({
    queryKey: [...scorecardKeys.moderationQueue(), params],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getModerationQueue(token, params);
    },
    enabled: isSignedIn,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Approve a scorecard (admin only).
 */
export function useApproveScorecard() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scorecardId: number) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.approveScorecard(scorecardId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.moderationQueue() });
      queryClient.invalidateQueries({ queryKey: scorecardKeys.all });
    },
  });
}

/**
 * Reject a scorecard (admin only).
 */
export function useRejectScorecard() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scorecardId, reason }: { scorecardId: number; reason: string }) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.rejectScorecard(scorecardId, reason, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.moderationQueue() });
    },
  });
}

/**
 * Remove featured status from a scorecard (admin only).
 */
export function useUnfeatureScorecard() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scorecardId: number) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.unfeatureScorecard(scorecardId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.featured() });
      queryClient.invalidateQueries({ queryKey: scorecardKeys.all });
    },
  });
}

/**
 * Feature a scorecard with editorial blurb (admin).
 */
export function useFeatureWithEditorial() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      scorecardId,
      editorial,
      template_style,
      editor_caption,
      combo_layout,
    }: {
      scorecardId: number;
      editorial: string | null;
      template_style?: string;
      editor_caption?: string | null;
      combo_layout?: string;
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.featureWithEditorial(
        scorecardId,
        { editorial, template_style, editor_caption, combo_layout },
        token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.featured() });
    },
  });
}

/**
 * Get archive spotlight schedule for a week (admin).
 */
export function useSpotlightWeek(weekStart: string) {
  const { getToken, isSignedIn } = useAuth();

  return useQuery({
    queryKey: scorecardKeys.spotlightWeek(weekStart),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.getSpotlightWeek(weekStart, token);
    },
    enabled: isSignedIn && !!weekStart,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Create archive spotlight (admin).
 */
export function useCreateSpotlight() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      spotlight_date: string;
      scorecard_id: number;
      editorial?: string;
      spotlight_type?: string;
      description?: string;
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.createSpotlight(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.all });
    },
  });
}

/**
 * Update archive spotlight (admin).
 */
export function useUpdateSpotlight() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      spotlightId,
      data,
    }: {
      spotlightId: number;
      data: {
        editorial?: string;
        scorecard_id?: number;
        spotlight_type?: string;
        description?: string;
      };
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.updateSpotlight(spotlightId, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.all });
    },
  });
}

/**
 * Delete archive spotlight (admin).
 */
export function useDeleteSpotlight() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (spotlightId: number) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.deleteSpotlight(spotlightId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.all });
    },
  });
}

/**
 * Get auto-suggested games for the Spotlight (admin).
 */
export function useSpotlightSuggestions(targetDate: string) {
  const { getToken, isSignedIn } = useAuth();

  return useQuery({
    queryKey: scorecardKeys.spotlightSuggestions(targetDate),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.getSpotlightSuggestions(token, targetDate);
    },
    enabled: isSignedIn && !!targetDate,
    staleTime: STALE_TIMES.SCHEDULE,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch recent scorecards for games with matching moment tags (admin only).
 */
export function useRecentMomentUploads(
  category: 'milestone' | 'non_milestone',
  limit: number = 20
) {
  const { getToken, isSignedIn } = useAuth();

  return useQuery({
    queryKey: scorecardKeys.momentUploads(category),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.getRecentMomentUploads(token, category, limit);
    },
    enabled: isSignedIn,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

// =============================================================================
// Editor's Pick Queue Hooks
// =============================================================================

/**
 * Fetch the Editor's Pick queue for the next N days (admin only).
 */
export function useEditorPickQueue(days: number = 3) {
  const { getToken, isSignedIn } = useAuth();

  return useQuery({
    queryKey: scorecardKeys.editorPickQueue(),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.getEditorPickQueue(token, days);
    },
    enabled: isSignedIn,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Schedule a scorecard as Editor's Pick for a specific date (admin only).
 */
export function useScheduleEditorPick() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: {
      scheduled_date: string;
      scorecard_id: number;
      editorial?: string | null;
      editor_caption?: string | null;
      template_style?: string | null;
      combo_layout?: string | null;
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.scheduleEditorPick(body, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.editorPickQueue() });
      queryClient.invalidateQueries({ queryKey: scorecardKeys.editorPickSuggestions() });
    },
  });
}

/**
 * Update a queued Editor's Pick (admin only).
 */
export function useUpdateEditorPick() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pickId,
      ...body
    }: {
      pickId: number;
      editorial?: string | null;
      editor_caption?: string | null;
      template_style?: string | null;
      combo_layout?: string | null;
      scorecard_id?: number | null;
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.updateEditorPick(pickId, body, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.editorPickQueue() });
    },
  });
}

/**
 * Delete a queued Editor's Pick (admin only).
 */
export function useDeleteEditorPick() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pickId: number) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.deleteEditorPick(pickId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.editorPickQueue() });
      queryClient.invalidateQueries({ queryKey: scorecardKeys.editorPickSuggestions() });
    },
  });
}

/**
 * Manually publish a queued Editor's Pick (admin only).
 */
export function usePublishEditorPick() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pickId: number) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.publishEditorPick(pickId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.editorPickQueue() });
      queryClient.invalidateQueries({ queryKey: scorecardKeys.featured() });
    },
  });
}

/**
 * Fetch Editor's Pick suggestions based on curation rules (admin only).
 */
export function useEditorPickSuggestions(limit: number = 10) {
  const { getToken, isSignedIn } = useAuth();

  return useQuery({
    queryKey: scorecardKeys.editorPickSuggestions(),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.getEditorPickSuggestions(token, limit);
    },
    enabled: isSignedIn,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch flagged comments (admin only).
 */
export function useFlaggedComments(params: { limit?: number; offset?: number } = {}) {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<FlaggedCommentsResponse>({
    queryKey: [...scorecardKeys.flaggedComments(), params],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getFlaggedComments(token, params);
    },
    enabled: isSignedIn,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch moderation history grouped by user (admin only).
 */
export function useModerationHistory() {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<ModerationHistoryResponse>({
    queryKey: [...scorecardKeys.all, 'moderation-history'],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getModerationHistory(token);
    },
    enabled: isSignedIn,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Moderate a comment (admin only).
 */
export function useModerateComment() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      action,
    }: {
      commentId: number;
      action: 'approve' | 'hide' | 'delete';
    }) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.moderateComment(commentId, action, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardKeys.flaggedComments() });
      queryClient.invalidateQueries({ queryKey: scorecardKeys.all });
    },
  });
}
